import {
  getRequestHeader,
  handleResponseError,
} from "@api/internal/utils/helpers";
import { ResponseData } from "@api/internal/utils/types";
import {
  BT_BACKEND_API_ENDPOINTS_SCHEMA,
  BTBackendAPIEndpoints,
  BTBackendAPIEndpointsKeys,
} from "@backend/utils/constants";
import { tryCatch } from "@typings/TryCatch";
import { BT_API_URL } from "@util/constant";
import axios, { HttpStatusCode } from "axios";

/**
 * A higher-order function that creates a typed fetcher for the BT backend API.
 * This function uses currying to allow specifying the response type first,
 * while inferring the endpoint and payload types.
 *
 * @docs https://sph.atlassian.net/wiki/x/FgD7n
 *
 * @param payload - The payload to send to the BT backend API.
 * @param endPointType - The endpoint to fetch data from.
 *
 * @returns An async function that takes an endpoint and a payload, and fetches data from the BT backend API.
 */
export const fetchBTBackendAPI = <ResponseType>() => {
  return async <Endpoint extends BTBackendAPIEndpointsKeys>(
    endPointType: Endpoint,
    payload: BTBackendAPIEndpoints[Endpoint]
  ): Promise<ResponseData<ResponseType>> => {
    const parsedSchema = checkPayloadIsValid(endPointType, payload);

    if (!parsedSchema.isValid) {
      const errorResponse = handleResponseError({
        message: "fetchBTBackendAPI: Payload structure is invalid",
        error: {
          code: HttpStatusCode.BadRequest,
          message: parsedSchema.error,
        },
      });

      return errorResponse;
    }

    const headers = await getRequestHeader();

    if (typeof headers === "undefined") {
      const errorResponse = handleResponseError({
        message: "fetchBTBackendAPI: BT_API_KEY is not found in secrets",
        error: {
          code: HttpStatusCode.InternalServerError,
          message: "BT_API_KEY is not found in secrets",
        },
      });

      return errorResponse;
    }

    const requestType =
      BT_BACKEND_API_ENDPOINTS_SCHEMA[endPointType].requestType;
    const apiEndPoint = endPointType;

    const { data: response, error } = await tryCatch(
      axios.request<ResponseData<ResponseType>>({
        method: requestType,
        url: BT_API_URL + apiEndPoint,
        headers,
        ...(requestType === "GET" ? { params: payload } : { data: payload }),
      })
    );

    if (error) {
      const errorResponse = handleResponseError({
        message: `fetchBTBackendAPI: Error calling endpoint ${JSON.stringify({ apiEndPoint, payload })}`,
        error: {
          code: HttpStatusCode.InternalServerError,
          message: JSON.stringify(error),
        },
      });

      return errorResponse;
    }

    const data = response.data;

    if (!data.success) {
      const errorResponse = handleResponseError({
        message: `fetchBTBackendAPI: API returned false ${JSON.stringify({ apiEndPoint, payload })}`,
        error: data.error,
        shouldLogError: false,
      });

      return errorResponse;
    }

    return data;
  };
};

const checkPayloadIsValid = <Endpoint extends BTBackendAPIEndpointsKeys>(
  endPointType: Endpoint,
  payload: BTBackendAPIEndpoints[Endpoint]
): { isValid: true } | { isValid: false; error: string } => {
  const schema = BT_BACKEND_API_ENDPOINTS_SCHEMA[endPointType].schema;
  const parsedPayload = schema.safeParse(payload);

  if (!parsedPayload.success) {
    return {
      isValid: false,
      error:
        "Payload structure is invalid: " +
        JSON.stringify(parsedPayload.error.issues),
    };
  }

  return { isValid: true };
};
